// packages/ui-web/src/_lib/reader-theme.js
//
// Reader theme module — browser-only apply helpers, plus a re-export of
// the SCHEMES/STYLES palettes that live in @mindex/core/reader-theme.
//
// Persistence is OWNED BY CALLERS — applyScheme/applyStyle here just write
// CSS custom properties; localStorage keys live in app.jsx (for the SPA)
// and share-reader.entry.jsx (for the share recipient). This is deliberate:
// the share-reader uses a separate key namespace so a recipient on a shared
// device doesn't pollute the owner's SPA preferences.

import { SCHEMES, STYLES } from '@mindex/core/reader-theme';
export { SCHEMES, STYLES };

function getDefaultRoot() {
  // Lazy lookup — never touches `document` at module load so the file is
  // safe to import from Node-side test/server code that only needs the
  // CSS builders below.
  return (typeof document !== 'undefined') ? document.documentElement : null;
}

function setSchemeVars(root, s) {
  const r = root.style;
  r.setProperty('--d-bg',         s.nav.bg);
  r.setProperty('--d-bg-elev',    s.nav.bgElev);
  r.setProperty('--d-bg-hover',   s.nav.bgHover);
  r.setProperty('--d-bg-active',  s.nav.bgActive);
  r.setProperty('--d-text',       s.nav.text);
  r.setProperty('--d-text-soft',  s.nav.textSoft);
  r.setProperty('--d-text-mute',  s.nav.textMute);
  r.setProperty('--d-border',     s.nav.border);
  r.setProperty('--d-rule',       s.nav.rule);
  r.setProperty('--r-bg',         s.reader.bg);
  r.setProperty('--r-bg-soft',    s.reader.bgSoft);
  r.setProperty('--r-text',       s.reader.text);
  r.setProperty('--r-text-soft',  s.reader.textSoft);
  r.setProperty('--r-text-mute',  s.reader.textMute);
  r.setProperty('--r-border',     s.reader.border);
  r.setProperty('--r-code-bg',    s.reader.codeBg);
}

function setStyleVars(root, s) {
  const r = root.style;
  r.setProperty('--reader-display',         s.display);
  r.setProperty('--reader-display-style',   s.displayStyle);
  r.setProperty('--reader-body',            s.body);
  r.setProperty('--reader-mono',            s.mono);
  r.setProperty('--reader-h1-size',         s.h1Size);
  r.setProperty('--reader-h1-weight',       String(s.h1Weight));
  r.setProperty('--reader-h2-size',         s.h2Size);
  r.setProperty('--reader-h2-weight',       String(s.h2Weight));
  r.setProperty('--reader-h3-size',         s.h3Size);
  r.setProperty('--reader-h3-weight',       String(s.h3Weight));
  r.setProperty('--reader-body-size',       s.bodySize);
  r.setProperty('--reader-line-height',     String(s.lineHeight));
  r.setProperty('--reader-letter-spacing',  s.letterSpacing);
}

export function applyScheme(key, root) {
  const s = SCHEMES[key];
  if (!s) return;
  const target = root ?? getDefaultRoot();
  if (!target) return;
  setSchemeVars(target, s);
  if (target.dataset) {
    target.dataset.rMode = (key === 'carbon' || key === 'inverse') ? 'dark' : 'light';
  }
}

export function applyStyle(key, root) {
  const s = STYLES[key];
  if (!s) return;
  const target = root ?? getDefaultRoot();
  if (!target) return;
  setStyleVars(target, s);
}

// Note: buildSchemeCss / buildStyleCss live in @mindex/core/reader-theme —
// the SPA never needs to emit CSS strings at runtime.
